import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import React from 'react';
import {
  INTEGRATION_CATEGORY_LABELS,
  type IntegrationCategory,
} from '../../ThirdPartyIntegration/categories';
import ProviderIconBadge from '../../ThirdPartyIntegration/common/ProviderIconBadge';
import type {
  IntegrationProviderConfig,
  ProviderIconComponent,
} from '../../ThirdPartyIntegration/types';

interface IntegrationCardProps {
  config: IntegrationProviderConfig;
  connectionCount: number;
  isDark: boolean;
  onSelect: (providerId: string) => void;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  config,
  connectionCount,
  isDark,
  onSelect,
}) => {
  const cardBg = isDark ? 'rgba(16, 24, 40, 0.45)' : '#FFFFFF';
  const cardBorder = isDark
    ? '1px solid rgba(71, 84, 103, 0.5)'
    : '1px solid #EAECF0';
  const cardHoverBorder = isDark ? '#7F56D9' : '#6941C6';
  const titleColor = isDark ? '#F9FAFB' : '#101828';
  const subtitleColor = isDark ? '#98A2B3' : '#667085';
  const accentColor = isDark ? '#7F56D9' : '#6941C6';

  const rawIcon = config.icon;
  const IconComponent: ProviderIconComponent | null =
    rawIcon && typeof rawIcon !== 'string'
      ? (rawIcon as ProviderIconComponent)
      : null;
  const brandColor = config.brandColor ?? accentColor;
  const isConnected = connectionCount > 0;
  const primaryCategory = (config.category?.[0] ??
    null) as IntegrationCategory | null;

  return (
    <ButtonBase
      onClick={() => onSelect(config.id)}
      sx={{
        width: '100%',
        textAlign: 'left',
        borderRadius: '12px',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 168,
          backgroundColor: cardBg,
          border: cardBorder,
          borderRadius: '12px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          transition: 'border-color 120ms ease, transform 120ms ease',
          '&:hover': {
            borderColor: cardHoverBorder,
            transform: 'translateY(-1px)',
          },
        }}
      >
        <Stack direction='row' alignItems='center' spacing={1.5}>
          <ProviderIconBadge
            IconComponent={IconComponent}
            brandColor={brandColor}
            size={40}
            isDark={isDark}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: 600,
                color: titleColor,
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {config.name}
            </Typography>
            {primaryCategory && (
              <Typography
                sx={{
                  fontSize: '11px',
                  color: subtitleColor,
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                  fontWeight: 500,
                }}
              >
                {INTEGRATION_CATEGORY_LABELS[primaryCategory]}
              </Typography>
            )}
          </Box>
        </Stack>

        <Typography
          sx={{
            fontSize: '13px',
            color: subtitleColor,
            lineHeight: 1.45,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            flex: 1,
          }}
        >
          {config.description ?? `Connect ${config.name} to your workflows.`}
        </Typography>

        <Stack direction='row' alignItems='center' spacing={1}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: isConnected ? '#12B76A' : '#D0D5DD',
            }}
          />
          <Typography
            sx={{
              fontSize: '12px',
              color: isConnected ? '#067647' : subtitleColor,
              fontWeight: 500,
            }}
          >
            {isConnected
              ? connectionCount === 1
                ? '1 connection'
                : `${connectionCount} connections`
              : 'Not connected'}
          </Typography>
        </Stack>
      </Box>
    </ButtonBase>
  );
};

export default IntegrationCard;
